import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { config } from '../config/environment';

type Status = 'connecting' | 'redirecting' | 'no_consent' | 'enabling_consent' | 'error' | 'wrong_role';

export default function SessionJoinPage() {
  const [searchParams] = useSearchParams();
  const meetingUrl = searchParams.get('meeting_url') || '';
  const appointmentId = searchParams.get('appointment_id') || '';
  const name = searchParams.get('name') || 'session';

  const [status, setStatus] = useState<Status>('connecting');
  const [errorMsg, setErrorMsg] = useState('');
  const [consentError, setConsentError] = useState('');
  const [wrongRoleCountdown, setWrongRoleCountdown] = useState(5);
  const called = useRef(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  });

  const callBot = async () => {
    setStatus('connecting');
    setErrorMsg('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${config.apiBaseUrl}/session/bot`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          meeting_url: meetingUrl,
          ...(appointmentId ? { appointment_id: appointmentId } : {}),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setStatus('redirecting');
        window.location.href = meetingUrl;
      } else if (res.status === 403) {
        const data403 = await res.json().catch(() => ({}));
        const detail403 = ((data403 as { detail?: string }).detail || '').toLowerCase();
        if (detail403.includes('coach access required') || detail403.includes('client access')) {
          setStatus('wrong_role');
        } else {
          setStatus('no_consent');
        }
      } else if (res.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userType');
        window.location.href = '/login?role=coach';
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMsg((data as { detail?: string }).detail || 'Recording bot could not be invited.');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        setStatus('error');
        setErrorMsg('Request timed out. Check your connection and try again.');
      } else {
        setStatus('error');
        setErrorMsg('Could not reach the server. Check your connection and try again.');
      }
    }
  };

  const enableConsent = async () => {
    if (!appointmentId) return;
    setConsentError('');
    setStatus('enabling_consent');
    try {
      const res = await fetch(`${config.apiBaseUrl}/coaches/sessions/${appointmentId}/ai-consent`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (res.ok) {
        await callBot();
      } else {
        setStatus('no_consent');
        setConsentError('Could not enable AI consent. Try again.');
      }
    } catch {
      setStatus('no_consent');
      setConsentError('Could not reach the server.');
    }
  };

  useEffect(() => {
    if (called.current || !meetingUrl) return;
    called.current = true;
    callBot();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status !== 'wrong_role') return;
    setWrongRoleCountdown(5);
    const interval = setInterval(() => {
      setWrongRoleCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.location.href = meetingUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [status, meetingUrl]);

  if (!meetingUrl) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-red-400 text-sm">No meeting URL provided.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-8 text-center">
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Joining</p>
        <h1 className="text-base font-semibold text-white mb-8 truncate">{name}</h1>

        {(status === 'connecting' || status === 'enabling_consent') && (
          <>
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-400">
              {status === 'enabling_consent' ? 'Enabling AI consent...' : 'Inviting recording bot...'}
            </p>
          </>
        )}

        {status === 'redirecting' && (
          <>
            <StatusIcon color="green" icon="check" />
            <p className="text-sm text-gray-400">Bot invited. Opening your meeting...</p>
          </>
        )}

        {status === 'no_consent' && (
          <>
            <StatusIcon color="amber" icon="warn" />
            <p className="text-sm text-amber-300 font-medium mb-1">Client hasn't consented to AI recording</p>
            <p className="text-xs text-gray-500 mb-6">
              Enable AI consent for this client to record and transcribe the session.
            </p>
            {consentError && (
              <p className="text-xs text-red-400 mb-4">{consentError}</p>
            )}
            <div className="flex flex-col gap-2">
              {appointmentId && (
                <button
                  onClick={enableConsent}
                  className="w-full py-2.5 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
                >
                  Enable AI consent and record
                </button>
              )}
              <button
                onClick={() => { window.location.href = meetingUrl; }}
                className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Join without recording
              </button>
            </div>
          </>
        )}

        {status === 'wrong_role' && (
          <>
            <StatusIcon color="amber" icon="warn" />
            <p className="text-sm text-amber-300 font-medium mb-1">Logged in as a client</p>
            <p className="text-xs text-gray-500 mb-6">
              {wrongRoleCountdown > 0
                ? `Opening meeting in ${wrongRoleCountdown}s...`
                : 'Opening meeting...'}
            </p>
            <button
              onClick={() => { window.location.href = meetingUrl; }}
              className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Join now
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <StatusIcon color="red" icon="x" />
            <p className="text-sm text-red-300 font-medium mb-1">Bot invite failed</p>
            <p className="text-xs text-gray-500 mb-6">{errorMsg}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={callBot}
                className="w-full py-2.5 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => { window.location.href = meetingUrl; }}
                className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Join without recording
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ color, icon }: { color: 'green' | 'amber' | 'red'; icon: 'check' | 'warn' | 'x' }) {
  const bg = { green: 'bg-green-500/10', amber: 'bg-amber-500/10', red: 'bg-red-500/10' }[color];
  const stroke = { green: 'text-green-400', amber: 'text-amber-400', red: 'text-red-400' }[color];
  const path =
    icon === 'check' ? 'M5 13l4 4L19 7' :
    icon === 'warn'  ? 'M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z' :
                       'M6 18L18 6M6 6l12 12';
  return (
    <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center mx-auto mb-4`}>
      <svg className={`w-5 h-5 ${stroke}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
      </svg>
    </div>
  );
}
